import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Turnstile } from "@/components/Turnstile";
import { authClient } from "@/lib/auth";

/**
 * AuthPage — unified login/signup, replacing the old separate LoginPage
 * and SignupPage (2026-09-02, direct operator directive: "one page
 * instead of two"). Everyone starts on the exact same screen — GitHub,
 * Google, and an email/password form, no marketing copy, no upfront
 * choice between "sign in" and "sign up". The account's existence
 * decides which one happens:
 *
 *   - Email/password: submitting calls POST /api/auth/check-email first
 *     (new endpoint, backend/routes/auth.ts) to find out whether this is
 *     a login or a signup BEFORE asking for anything else. An existing
 *     email attempts sign-in immediately with the password already
 *     typed; a new one reveals the signup step (name + access code)
 *     right there on the same page.
 *   - GitHub/Google: clicking either attempts sign-in directly. An
 *     existing account just logs in. A brand-new one hits
 *     backend/auth/index.ts's databaseHooks.user.create.before, which
 *     requires an access code and — since OAuth is a full-page redirect,
 *     not something this page's own JS can catch inline — bounces back
 *     here via errorCallbackURL so we can reveal the access-code step
 *     and let them retry the same provider once it's verified.
 *
 * The early-access access code (signup_codes table) is the ONLY gate on
 * account creation — there is no separate "verification code" concept
 * beyond it. Email/password accounts still go through Better Auth's own
 * post-signup email-verification link (unchanged, requireEmailVerification:
 * true) before they can log in; OAuth accounts skip that since the
 * provider already vouches for the email address — both are Better
 * Auth's existing behavior, not something this page implements itself.
 */

type Phase =
  "start" | "signup-details" | "oauth-access-code" | "check-email-sent";

function navigateToReturnUrl(
  returnUrl: string,
  navigate: (path: string) => void,
) {
  // Cross-origin, a server route (/api/*), or any absolute URL needs a
  // hard navigation so the auth cookie/token actually lands there — same
  // logic the old LoginPage used for the management.pure.dev SSO bounce
  // (?return=<url>). An internal SPA path can go through react-router.
  try {
    const u = new URL(returnUrl, window.location.origin);
    const isCrossOrigin = u.origin !== window.location.origin;
    const isServerRoute = u.pathname.startsWith("/api/");
    const isAbsolute = /^https?:/i.test(returnUrl);
    if (isCrossOrigin || isServerRoute || isAbsolute) {
      window.location.assign(u.toString());
      return;
    }
  } catch {
    /* not a URL — treat as internal path */
  }
  navigate(returnUrl);
}

export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnUrl = searchParams.get("return") ?? "/dashboard";

  const [phase, setPhase] = useState<Phase>("start");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [signupCode, setSignupCode] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oauthProvider, setOauthProvider] = useState<
    "github" | "google" | null
  >(null);
  // Which social buttons the backend can actually back — see backend/
  // routes/auth.ts's GET /api/auth/providers. Starts empty (both hidden)
  // rather than defaulting to "assume configured": a provider whose
  // client id/secret aren't set fails on click with no useful error, so
  // showing nothing until we know beats showing a dead button.
  const [availableProviders, setAvailableProviders] = useState<{
    github: boolean;
    google: boolean;
  }>({
    github: false,
    google: false,
  });

  useEffect(() => {
    fetch("/api/auth/providers")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { github?: boolean; google?: boolean } | null) => {
        if (data)
          setAvailableProviders({
            github: Boolean(data.github),
            google: Boolean(data.google),
          });
      })
      .catch(() => {
        /* leave both hidden — same fail-safe as the initial state */
      });
  }, []);

  // An OAuth signup blocked for lack of an access code lands back here as
  // ?error=<message, spaces as underscores>&provider=<github|google> —
  // see this file's own header comment for why a redirect, not an inline
  // catch. Anything else in ?error= (a real OAuth failure — denied
  // consent, provider outage) just surfaces as a plain error banner on
  // the start phase.
  useEffect(() => {
    const rawError = searchParams.get("error");
    if (!rawError) return;
    const provider = searchParams.get("provider");
    const message = rawError.replace(/_/g, " ");
    if (
      message.toLowerCase().includes("access code") &&
      (provider === "github" || provider === "google")
    ) {
      setOauthProvider(provider);
      setPhase("oauth-access-code");
    } else {
      setError(message);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    next.delete("provider");
    setSearchParams(next, { replace: true });
    // Only ever meant to run once, against whatever query params the
    // page loaded with — re-running on every searchParams change would
    // immediately re-trigger on the very setSearchParams call above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildErrorCallbackUrl(provider: "github" | "google"): string {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set("provider", provider);
    if (searchParams.get("return")) url.searchParams.set("return", returnUrl);
    return url.toString();
  }

  async function signInWith(provider: "github" | "google") {
    setError(null);
    try {
      // callbackURL/errorCallbackURL both travel through the OAuth
      // redirect — better-auth validates errorCallbackURL against
      // trustedOrigins, so this must be absolute (buildErrorCallbackUrl
      // above), not a bare path.
      await authClient.signIn.social({
        provider,
        callbackURL: returnUrl,
        errorCallbackURL: buildErrorCallbackUrl(provider),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "OAuth redirect failed");
    }
  }

  async function attemptSignIn() {
    const res = await authClient.signIn.email({ email, password });
    if (res.error) {
      setError(res.error.message ?? "Incorrect email or password");
      return;
    }
    const token = (res.data as unknown as { token?: string })?.token ?? null;
    if (typeof token !== "string") {
      setError("login succeeded but no token was returned");
      return;
    }
    const { setToken } = await import("@/lib/sw");
    await setToken(token);
    navigateToReturnUrl(returnUrl, navigate);
  }

  async function onSubmitStart(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const checkRes = await fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!checkRes.ok) {
        setError("Could not check that email — try again.");
        return;
      }
      const { exists } = (await checkRes.json()) as { exists: boolean };
      if (exists) {
        await attemptSignIn();
      } else {
        setPhase("signup-details");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitSignupDetails(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Better Auth's client signUp helper doesn't support the extra
      // turnstileToken/signupCode fields — raw fetch instead, same as
      // the old SignupPage did.
      const res = await fetch("/api/auth/sign-up/email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          name,
          turnstileToken,
          signupCode,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
          message?: string;
        } | null;
        setError(
          body?.error ?? body?.message ?? `Sign up failed (${res.status})`,
        );
        return;
      }
      setPhase("check-email-sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign up failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitOauthCode(e: React.FormEvent) {
    e.preventDefault();
    if (!oauthProvider) return;
    setError(null);
    setSubmitting(true);
    try {
      // Non-consuming check that stashes the code in a short-lived
      // cookie (backend/routes/auth.ts) — databaseHooks.user.create.before
      // reads and actually consumes it on the retried OAuth callback.
      const res = await fetch("/api/auth/verify-signup-code", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ signupCode, turnstileToken }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        setError(body?.error ?? `Invalid code (${res.status})`);
        return;
      }
      await signInWith(oauthProvider);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify code");
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === "check-email-sent") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Check your email</CardTitle>
            <CardDescription>
              We sent a verification link to <strong>{email}</strong>. Click it
              to finish creating your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link
              to="/login"
              className="text-sm underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === "oauth-access-code") {
    const providerLabel = oauthProvider === "github" ? "GitHub" : "Google";
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Enter your access code</CardTitle>
            <CardDescription>
              We&apos;re in early access — there&apos;s no account for that{" "}
              {providerLabel} identity yet, so an access code is needed to
              create one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onSubmitOauthCode} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="oauthSignupCode">Access code</Label>
                <Input
                  id="oauthSignupCode"
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value)}
                  placeholder="XXXX-XXXX"
                  required
                  autoFocus
                />
              </div>
              <Turnstile onToken={setTurnstileToken} />
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Continuing…" : `Continue with ${providerLabel}`}
              </Button>
            </form>
            <div className="text-sm text-center">
              <button
                type="button"
                onClick={() => {
                  setPhase("start");
                  setOauthProvider(null);
                  setError(null);
                }}
                className="font-medium underline-offset-4 hover:underline"
              >
                Back
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === "signup-details") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Create your account</CardTitle>
            <CardDescription>
              No account exists yet for <strong>{email}</strong> — we&apos;re in
              early access, so an access code is needed too.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onSubmitSignupDetails} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signupPassword">Password</Label>
                <Input
                  id="signupPassword"
                  type="password"
                  minLength={10}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  At least 10 characters.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signupCode">Access code</Label>
                <Input
                  id="signupCode"
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value)}
                  placeholder="XXXX-XXXX"
                  required
                />
              </div>
              <Turnstile onToken={setTurnstileToken} />
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Creating…" : "Create account"}
              </Button>
            </form>
            <div className="text-sm text-center">
              <button
                type="button"
                onClick={() => {
                  setPhase("start");
                  setError(null);
                }}
                className="font-medium underline-offset-4 hover:underline"
              >
                Back
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Continue to Pure</CardTitle>
          <CardDescription>
            Sign in, or create an account if you don&apos;t have one yet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(availableProviders.github || availableProviders.google) && (
            <div
              className={
                availableProviders.github && availableProviders.google
                  ? "grid grid-cols-2 gap-2"
                  : "grid grid-cols-1 gap-2"
              }
            >
              {availableProviders.github && (
                <Button
                  variant="outline"
                  onClick={() => signInWith("github")}
                  type="button"
                >
                  GitHub
                </Button>
              )}
              {availableProviders.google && (
                <Button
                  variant="outline"
                  onClick={() => signInWith("google")}
                  type="button"
                >
                  Google
                </Button>
              )}
            </div>
          )}
          {(availableProviders.github || availableProviders.google) && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>
          )}
          <form onSubmit={onSubmitStart} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Continuing…" : "Continue"}
            </Button>
          </form>
          <div className="text-sm text-muted-foreground text-center">
            <Link
              to="/reset-password"
              className="underline-offset-4 hover:underline"
            >
              Forgot your password?
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
